export type RecaptchaTheme = "dark" | "light";
export type RecaptchaSize = "compact" | "normal" | "invisible";
export type RecaptchaRenderOptions = {
  sitekey: string;
  theme?: RecaptchaTheme;
  size?: RecaptchaSize;
  tabindex?: number;
};
export type RecaptchaScriptLoadOptions = {
  language?: string;
};

const RECAPTCHA_SCRIPT_URL = "https://www.google.com/recaptcha/api.js";
const RECAPTCHA_SCRIPT_ID = "recaptcha-script";

declare global {
  interface Window {
    grecaptcha: {
      render: (...args: unknown[]) => string;
      execute: (token?: string | null) => void;
      reset: (token?: string | null) => void;
    };
    recaptchaReady: () => unknown | undefined;
  }
}

export function executeRecaptcha(widgetId?: string | null) {
  window.grecaptcha.execute(widgetId);
}

export function resetRecaptcha(widgetId?: string | null) {
  window.grecaptcha.reset(widgetId);
}

/**
 * Append the recaptcha script to the document and wait until loaded.
 *
 * @returns {Promise} - A promise that resolves once the script has loaded.
 */
export function loadRecaptchaScript(
  options?: RecaptchaScriptLoadOptions
): Promise<void> {
  if (window.grecaptcha) {
    return Promise.resolve();
  }

  const { language } = options ?? {};

  return new Promise((resolve, reject) => {
    // Resolve when scripts load.
    window.recaptchaReady = resolve;

    // Remove script on failure.
    const loadingFailed = (reason: string) => {
      document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
      reject(new Error(reason));
    };

    const url = new URL(RECAPTCHA_SCRIPT_URL);
    url.searchParams.append("onload", "recaptchaReady");
    url.searchParams.append("render", "explicit");

    if (language) {
      url.searchParams.append("hl", language);
    }

    const doc = window.document;
    const scriptTag = doc.createElement("script");

    scriptTag.setAttribute("id", RECAPTCHA_SCRIPT_ID);
    scriptTag.setAttribute("src", url.toString());
    scriptTag.setAttribute("async", "");
    scriptTag.setAttribute("defer", "");
    scriptTag.onerror = () => loadingFailed("error");
    scriptTag.onabort = () => loadingFailed("aborted");

    doc.head.appendChild(scriptTag);
  });
}

/**
 * Render the recaptcha widget.
 * You need to ensure the recaptcha API is loaded with {@link loadRecaptchaScript} before calling this function.
 *
 * @param container - The widget HTMLElement or ID.
 * @param options - Rendering options. The `options.sitekey` is required.
 * @param options.sitekey - Required.
 * @param options.theme - Optional. The color theme of the widget. Default to `light`.
 * @param options.size - Optional. The size of the widget. Default to `normal`.
 * @param options.tabindex - Optional. The tabindex of the widget and challenge. Default to `0`.
 * @param onSuccess - Executed when the user submits a successful response. The g-recaptcha-response token is passed to your callback.
 * @param onExpired - Executed when the reCAPTCHA response expires and the user needs to re-verify.
 * @param onError - executed when reCAPTCHA encounters an error (usually network connectivity) and cannot continue until connectivity is restored.
 * @returns {String} - The widget ID.
 * @throws {Error} - If recaptcha script is not initialized.
 */
export function renderRecaptcha(
  container: Element | string,
  options: RecaptchaRenderOptions,
  onSuccess: (token: string) => unknown,
  onExpired: () => unknown,
  onError: () => unknown
): string {
  if (!window.grecaptcha) {
    throw "Recaptcha not initialized.";
  }

  const { sitekey, theme, size, tabindex } = options;

  return window.grecaptcha.render(container, {
    sitekey,
    theme,
    size,
    tabindex,
    callback: onSuccess,
    "expired-callback": onExpired,
    "error-callback": onError,
  });
}
